import React, { useMemo, useState } from 'react';
import { useReducedMotion } from '../../hooks/useReducedMotion';

/**
 * Event Briefing — the "cheat sheet" an organizer gets when they start working
 * on an event. It is a quiet, point-in-time snapshot assembled client-side from
 * data the dashboard already has (the event summary + onboarding gaps): no extra
 * round-trip.
 *
 * It reads the current state of the event and tells the organizer three things
 * at a glance:
 *   1. WHAT this event is (name, type, when it happens relative to now)
 *   2. WHERE it stands right now (a one-line headline + a few key stats)
 *   3. WHAT to do next (the single highest-priority open gap)
 *
 * Behavior (option B): it shows once per event per session as a full banner.
 * Dismissing collapses it to a small, re-summonable pill — the organizer can
 * reopen the briefing any time without it nagging on every visit. Session
 * persistence is owned by the parent (keyed by eventId); this component just
 * renders the two states and reports open/close back up.
 */

export interface BriefingSummary {
  guestCount: number;
  seatCount: number;
  assignedSeats: number;
  checkedIn: number;
  ticketsSold: number;
  ticketRevenue: number;
}

export interface BriefingGap {
  key: string;
  label: string;
  done: boolean;
  detail: string;
  path: string;
}

export interface BriefingOnboarding {
  completed: number;
  total: number;
  percent: number;
  items: BriefingGap[];
}

export interface EventBriefingProps {
  eventName: string;
  isTicketed: boolean;
  date: string | null;
  summary: BriefingSummary | null;
  onboarding: BriefingOnboarding | null;
  /** Whether to render the full banner (true) or the collapsed pill (false). */
  open: boolean;
  /** Collapse the banner to the pill. */
  onDismiss: () => void;
  /** Re-expand from the pill back to the full banner. */
  onReopen: () => void;
  /** Jump to a path (used by the "do next" action). */
  onNavigate: (path: string) => void;
}

/** Human relative-time for a date-only ISO string, interpreted as a local day. */
function relativeTime(iso: string | null): { text: string; tone: 'future' | 'today' | 'past' | 'tbd' } {
  if (!iso) return { text: 'date not set', tone: 'tbd' };
  const datePart = iso.includes('T') ? iso.split('T')[0] : iso;
  const [y, m, d] = datePart.split('-').map(Number);
  if (!y || !m || !d) return { text: 'date not set', tone: 'tbd' };
  const event = new Date(y, m - 1, d);
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const msPerDay = 24 * 60 * 60 * 1000;
  const days = Math.round((event.getTime() - today.getTime()) / msPerDay);
  if (days === 0) return { text: 'happening today', tone: 'today' };
  if (days === 1) return { text: 'tomorrow', tone: 'future' };
  if (days === -1) return { text: 'yesterday', tone: 'past' };
  if (days > 1 && days < 7) return { text: `in ${days} days`, tone: 'future' };
  if (days >= 7 && days < 14) return { text: 'in about a week', tone: 'future' };
  if (days >= 14) {
    const weeks = Math.round(days / 7);
    return weeks < 9 ? { text: `in ${weeks} weeks`, tone: 'future' } : { text: `in ${Math.round(days / 30)} months`, tone: 'future' };
  }
  // days < -1 → past
  const past = Math.abs(days);
  if (past < 7) return { text: `${past} days ago`, tone: 'past' };
  return { text: 'past event', tone: 'past' };
}

function money(n: number): string {
  return `$${n.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
}

/**
 * The headline is the one thing most worth saying about this event right now.
 * It is derived from state in priority order so the organizer immediately knows
 * where their attention belongs.
 */
function buildHeadline(
  rel: { tone: 'future' | 'today' | 'past' | 'tbd' },
  summary: BriefingSummary | null,
  onboarding: BriefingOnboarding | null,
): string {
  if (rel.tone === 'past') {
    if (summary && summary.checkedIn > 0) return `This event has wrapped — ${summary.checkedIn} guests checked in.`;
    return 'This event has already taken place.';
  }
  if (rel.tone === 'today' && summary) {
    const seatedPct = summary.seatCount > 0 ? Math.round((summary.assignedSeats / summary.seatCount) * 100) : 0;
    if (seatedPct < 100 && summary.seatCount > 0) return `It's event day and seating is ${seatedPct}% assigned — finish placements before doors.`;
    return "It's event day — you're set up and ready for check-in.";
  }
  if (!summary || summary.guestCount === 0) {
    return 'No guests yet — the first move is building your guest list.';
  }
  const seatedPct = summary.seatCount > 0 ? Math.round((summary.assignedSeats / summary.seatCount) * 100) : 0;
  if (onboarding && onboarding.percent < 100 && onboarding.total > 0) {
    const left = onboarding.total - onboarding.completed;
    return `${onboarding.percent}% set up — ${left} step${left === 1 ? '' : 's'} left before this event is ready.`;
  }
  if (summary.seatCount > 0 && seatedPct < 100) {
    return `Setup is done — ${seatedPct}% of seats assigned. Keep placing guests.`;
  }
  return 'This event is in good shape — everything configured and ready.';
}

export function EventBriefing({
  eventName,
  isTicketed,
  date,
  summary,
  onboarding,
  open,
  onDismiss,
  onReopen,
  onNavigate,
}: EventBriefingProps) {
  const reduced = useReducedMotion();
  const rel = useMemo(() => relativeTime(date), [date]);
  const headline = useMemo(() => buildHeadline(rel, summary, onboarding), [rel, summary, onboarding]);
  const nextGap = useMemo(() => onboarding?.items.find((i) => !i.done) ?? null, [onboarding]);

  const seatedPct = summary && summary.seatCount > 0 ? Math.round((summary.assignedSeats / summary.seatCount) * 100) : 0;
  const checkInPct = summary && summary.guestCount > 0 ? Math.round((summary.checkedIn / summary.guestCount) * 100) : 0;

  if (!open) {
    return (
      <button
        type="button"
        style={styles.pill}
        onClick={onReopen}
        title="Reopen the event briefing"
        aria-label={`Reopen briefing for ${eventName}`}
      >
        <span aria-hidden>🧭</span>
        <span style={styles.pillText}>Briefing</span>
      </button>
    );
  }

  // Key stats shown in the banner. Revenue only matters for ticketed events;
  // free events get tickets-sold suppressed and lean on guests / seated / checked-in.
  const stats: { label: string; value: string }[] = [];
  if (summary) {
    stats.push({ label: 'Guests', value: summary.guestCount.toLocaleString() });
    if (summary.seatCount > 0) stats.push({ label: 'Seated', value: `${seatedPct}%` });
    if (rel.tone === 'today' || rel.tone === 'past' || summary.checkedIn > 0) {
      stats.push({ label: 'Checked in', value: `${checkInPct}%` });
    }
    if (isTicketed) {
      stats.push({ label: 'Revenue', value: money(summary.ticketRevenue) });
    }
  }

  return (
    <section
      style={{ ...styles.wrap, ...(reduced ? {} : styles.wrapAnim) }}
      aria-label={`Briefing for ${eventName}`}
    >
      {!reduced && (
        <style>{`
          @keyframes ep-briefing-in { from { opacity: 0; transform: translateY(-6px); } to { opacity: 1; transform: translateY(0); } }
        `}</style>
      )}
      <span aria-hidden style={styles.glow} />

      <div style={styles.topRow}>
        <div style={styles.kicker}>
          <span aria-hidden>🧭</span>
          <span>Event briefing</span>
        </div>
        <button
          type="button"
          style={styles.dismiss}
          onClick={onDismiss}
          title="Collapse briefing"
          aria-label="Collapse briefing"
        >
          ✕
        </button>
      </div>

      <div style={styles.identityRow}>
        <h2 style={styles.name}>{eventName}</h2>
        <span style={isTicketed ? styles.badgeTicketed : styles.badgeFree}>
          {isTicketed ? '🎟️ Ticketed' : '🎉 Free'}
        </span>
        <span style={{ ...styles.when, ...toneStyle(rel.tone) }}>{rel.text}</span>
      </div>

      <p style={styles.headline}>{headline}</p>

      {stats.length > 0 && (
        <div style={styles.statsRow}>
          {stats.map((s) => (
            <div key={s.label} style={styles.stat}>
              <span style={styles.statValue}>{s.value}</span>
              <span style={styles.statLabel}>{s.label}</span>
            </div>
          ))}
        </div>
      )}

      {nextGap ? (
        <button style={styles.next} onClick={() => onNavigate(nextGap.path)} title={nextGap.detail}>
          <span style={styles.nextTag}>Start here</span>
          <span style={styles.nextBody}>
            <span style={styles.nextLabel}>{nextGap.label}</span>
            <span style={styles.nextHint}>{nextGap.detail}</span>
          </span>
          <span style={styles.nextArrow}>→</span>
        </button>
      ) : (
        <div style={styles.allSet}>✓ Nothing outstanding — this event is fully configured.</div>
      )}
    </section>
  );
}

function toneStyle(tone: 'future' | 'today' | 'past' | 'tbd'): React.CSSProperties {
  switch (tone) {
    case 'today': return { background: 'rgba(201,168,76,0.22)', color: '#92400e' };
    case 'future': return { background: 'rgba(26,107,60,0.14)', color: '#166534' };
    case 'past': return { background: 'rgba(107,115,120,0.16)', color: '#4b5563' };
    default: return { background: 'rgba(107,115,120,0.12)', color: '#6b7280' };
  }
}

const GREEN = '#1a6b3c';
const styles: Record<string, React.CSSProperties> = {
  pill: {
    display: 'inline-flex', alignItems: 'center', gap: 7, marginBottom: 20,
    background: '#fff', color: GREEN, border: '1.5px solid #c3dccb', borderRadius: 999,
    padding: '7px 14px', fontSize: '0.82rem', fontWeight: 700, cursor: 'pointer',
    boxShadow: '0 2px 8px -4px rgba(26,107,60,0.4)',
  },
  pillText: { letterSpacing: '0.01em' },

  wrap: {
    position: 'relative', overflow: 'hidden', borderRadius: 18, padding: '18px 20px',
    marginBottom: 20, color: '#0f2d1f',
    background: 'linear-gradient(135deg, #ffffff 0%, #f3faf5 100%)',
    border: '1px solid #cfe6d8', boxShadow: '0 10px 30px -18px rgba(26,107,60,0.45)',
  },
  wrapAnim: { animation: 'ep-briefing-in 0.4s cubic-bezier(.2,.8,.2,1) both' },
  glow: {
    position: 'absolute', top: -70, right: -40, width: 220, height: 220, borderRadius: '50%',
    background: 'radial-gradient(circle, rgba(26,107,60,0.14), transparent 70%)',
    pointerEvents: 'none',
  },

  topRow: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', position: 'relative', zIndex: 1 },
  kicker: { display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.14em', textTransform: 'uppercase', color: GREEN },
  dismiss: { background: 'transparent', border: 'none', cursor: 'pointer', color: '#6b7280', fontSize: '0.9rem', lineHeight: 1, padding: 4, borderRadius: 6 },

  identityRow: { display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginTop: 8, position: 'relative', zIndex: 1 },
  name: { fontSize: '1.3rem', fontWeight: 800, margin: 0, letterSpacing: '-0.02em', color: '#0f2d1f' },
  badgeTicketed: { fontSize: '0.72rem', fontWeight: 700, color: '#1e40af', background: '#dbeafe', borderRadius: 999, padding: '3px 10px' },
  badgeFree: { fontSize: '0.72rem', fontWeight: 700, color: '#92400e', background: '#fef3c7', borderRadius: 999, padding: '3px 10px' },
  when: { fontSize: '0.74rem', fontWeight: 700, borderRadius: 999, padding: '3px 10px' },

  headline: { fontSize: '0.98rem', fontWeight: 600, color: '#15402b', margin: '10px 0 0', lineHeight: 1.5, maxWidth: 640, position: 'relative', zIndex: 1 },

  statsRow: { display: 'flex', gap: 22, flexWrap: 'wrap', marginTop: 14, position: 'relative', zIndex: 1 },
  stat: { display: 'flex', flexDirection: 'column' },
  statValue: { fontSize: '1.35rem', fontWeight: 800, color: '#0f2d1f', lineHeight: 1, fontVariantNumeric: 'tabular-nums' },
  statLabel: { fontSize: '0.72rem', fontWeight: 600, color: '#5b7366', marginTop: 3, textTransform: 'uppercase', letterSpacing: '0.04em' },

  next: { display: 'flex', alignItems: 'center', gap: 12, width: '100%', textAlign: 'left', marginTop: 16, background: '#fff', border: `1.5px solid ${GREEN}`, borderRadius: 12, padding: '11px 14px', cursor: 'pointer', boxShadow: '0 4px 14px -8px rgba(26,107,60,0.4)', position: 'relative', zIndex: 1 },
  nextTag: { flexShrink: 0, background: GREEN, color: '#fff', borderRadius: 999, padding: '4px 10px', fontSize: '0.66rem', fontWeight: 800, textTransform: 'uppercase', letterSpacing: '0.04em' },
  nextBody: { display: 'flex', flexDirection: 'column', flex: 1, minWidth: 0 },
  nextLabel: { fontWeight: 800, color: '#0f2d1f', fontSize: '0.92rem' },
  nextHint: { fontSize: '0.76rem', color: '#5b7366', marginTop: 1 },
  nextArrow: { color: GREEN, fontWeight: 800, fontSize: '1.2rem' },

  allSet: { marginTop: 14, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 10, padding: '10px 14px', color: '#065f46', fontSize: '0.86rem', fontWeight: 600, position: 'relative', zIndex: 1 },
};
